/**
 * 🔄 scrollToError: Utility functions for scrolling to form errors
 * Provides smooth scrolling to error alerts and invalid form fields
 */

/**
 * 🔄 scrollToElement: Scrolls to a specific element by ID with smooth animation
 * @param elementId - The ID of the element to scroll to
 * @param offset - Pixels to offset from the top (default: 100)
 * @returns boolean - true if element was found and scrolled to
 */
export const scrollToElement = (
  elementId: string,
  offset: number = 100
): boolean => {
  console.log('🔄 scrollToElement: Attempting to scroll to element', {
    elementId,
    offset,
  })

  const element = document.getElementById(elementId)
  if (!element) {
    console.warn('⚠️ scrollToElement: Element not found', { elementId })
    return false
  }

  // Find the scrollable parent container
  const findScrollableParent = (el: HTMLElement): HTMLElement | null => {
    let parent = el.parentElement
    while (parent) {
      const style = window.getComputedStyle(parent)
      if (
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll'
      ) {
        return parent
      }
      parent = parent.parentElement
    }
    return null
  }

  const scrollContainer = findScrollableParent(element) || window

  console.log('🔄 scrollToElement: Found scroll container', {
    isWindow: scrollContainer === window,
    containerTag:
      scrollContainer !== window
        ? (scrollContainer as HTMLElement).tagName
        : 'window',
  })

  // Calculate scroll position with proper offset
  try {
    const elementRect = element.getBoundingClientRect()

    if (scrollContainer === window) {
      // For window scrolling, calculate the target position with offset
      const currentScrollY =
        window.pageYOffset || document.documentElement.scrollTop
      const targetScrollY = currentScrollY + elementRect.top - offset

      window.scrollTo({
        top: Math.max(0, targetScrollY), // Prevent negative scroll
        behavior: 'smooth',
      })

      console.log(
        '✅ scrollToElement: Scrolled window to element with offset',
        {
          elementId,
          offset,
          targetScrollY: Math.max(0, targetScrollY),
        }
      )
    } else {
      // For container scrolling
      const container = scrollContainer as HTMLElement
      const containerRect = container.getBoundingClientRect()
      const relativeTop =
        elementRect.top - containerRect.top + container.scrollTop

      container.scrollTo({
        top: Math.max(0, relativeTop - offset),
        behavior: 'smooth',
      })

      console.log(
        '✅ scrollToElement: Scrolled container to element with offset',
        {
          elementId,
          offset,
          targetTop: Math.max(0, relativeTop - offset),
        }
      )
    }

    // Add focus to the first focusable element within the error field
    const focusableElements = element.querySelectorAll(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    )
    if (focusableElements.length > 0) {
      setTimeout(() => {
        ;(focusableElements[0] as HTMLElement).focus()
      }, 500)
    }

    return true
  } catch (e) {
    console.error('❌ scrollToElement: Error during scroll', e)

    // Final fallback: use scrollIntoView as last resort
    try {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })
      console.log('✅ scrollToElement: Used scrollIntoView fallback')
      return true
    } catch (fallbackError) {
      console.error('❌ scrollToElement: Fallback also failed', fallbackError)
      return false
    }
  }
}

/**
 * 🔄 scrollToFirstError: Scrolls to the topmost error field in the form
 * @param fieldErrors - Array of field IDs that have errors
 * @param customError - Map of field names to error messages
 * @param fieldPrefix - Prefix for field IDs (default: 'field-')
 * @returns boolean - true if an error field was found and scrolled to
 */
export const scrollToFirstError = (
  fieldErrors: string[],
  customError: Record<string, string>,
  fieldPrefix: string = 'field-'
): boolean => {
  console.log('🔄 scrollToFirstError: Looking for topmost error field', {
    fieldErrors,
    customErrorKeys: Object.keys(customError),
  })

  // Additional debug: check what elements with field- IDs exist on the page
  const allFieldElements = document.querySelectorAll('[id^="field-"]')
  console.log('🔄 scrollToFirstError: Available field elements on page', {
    elements: Array.from(allFieldElements).map((el) => ({
      id: el.id,
      dataFieldNames: el.getAttribute('data-field-names'),
    })),
  })

  // Extract field IDs from fieldErrors (they come as full paths like "form.group.fieldId" or just "fieldId")
  const fieldIds = fieldErrors.map((error) => {
    // Handle both "form.group.fieldId" and simple "fieldId" formats
    if (error.includes('.')) {
      const parts = error.split('.')
      return parts[parts.length - 1] // Get the last part which is the field ID
    }
    return error // Return as-is if no dots (e.g., "stateProvince", "team_name")
  })

  // Collect all error field elements with their positions
  interface ErrorFieldElement {
    element: HTMLElement
    fieldId: string
    yPosition: number
    elementId: string
  }

  const errorElements: ErrorFieldElement[] = []

  // Check fieldErrors for elements
  for (const fieldId of fieldIds) {
    const elementId = `${fieldPrefix}${fieldId}`
    const element = document.getElementById(elementId)

    if (element) {
      const rect = element.getBoundingClientRect()
      errorElements.push({
        element,
        fieldId,
        yPosition: rect.top + window.scrollY,
        elementId,
      })
      console.log('🔄 scrollToFirstError: Found error field element', {
        fieldId,
        elementId,
        yPosition: rect.top + window.scrollY,
      })
    }

    // Also check for elements that support multiple field names via data attribute
    const elementsWithDataFields = Array.from(
      document.querySelectorAll(`[data-field-names*="${fieldId}"]`)
    )
    for (const dataFieldElement of elementsWithDataFields) {
      if (dataFieldElement.id) {
        const rect = dataFieldElement.getBoundingClientRect()
        errorElements.push({
          element: dataFieldElement as HTMLElement,
          fieldId,
          yPosition: rect.top + window.scrollY,
          elementId: dataFieldElement.id,
        })
        console.log('🔄 scrollToFirstError: Found data-field-names element', {
          fieldId,
          elementId: dataFieldElement.id,
          yPosition: rect.top + window.scrollY,
        })
      }
    }
  }

  // Check customError keys for elements
  for (const errorKey of Object.keys(customError)) {
    // Extract field ID from error key (e.g., "form.team_info.123" -> "123")
    const parts = errorKey.split('.')
    const fieldId = parts[parts.length - 1]
    const elementId = `${fieldPrefix}${fieldId}`
    const element = document.getElementById(elementId)

    if (element) {
      const rect = element.getBoundingClientRect()
      errorElements.push({
        element,
        fieldId,
        yPosition: rect.top + window.scrollY,
        elementId,
      })
      console.log('🔄 scrollToFirstError: Found custom error field element', {
        errorKey,
        fieldId,
        elementId,
        yPosition: rect.top + window.scrollY,
      })
    }

    // Also check for elements that support multiple field names via data attribute for custom errors
    const elementsWithDataFields = Array.from(
      document.querySelectorAll(`[data-field-names*="${fieldId}"]`)
    )
    for (const customDataFieldElement of elementsWithDataFields) {
      if (customDataFieldElement.id) {
        const rect = customDataFieldElement.getBoundingClientRect()
        errorElements.push({
          element: customDataFieldElement as HTMLElement,
          fieldId,
          yPosition: rect.top + window.scrollY,
          elementId: customDataFieldElement.id,
        })
        console.log(
          '🔄 scrollToFirstError: Found custom error data-field-names element',
          {
            errorKey,
            fieldId,
            elementId: customDataFieldElement.id,
            yPosition: rect.top + window.scrollY,
          }
        )
      }
    }
  }

  // If we found error elements, sort by Y position and scroll to the topmost one
  if (errorElements.length > 0) {
    // Remove duplicates based on elementId
    const uniqueElements = errorElements.filter(
      (item, index, arr) =>
        arr.findIndex((el) => el.elementId === item.elementId) === index
    )

    // Sort by Y position (topmost first)
    uniqueElements.sort((a, b) => a.yPosition - b.yPosition)

    const topmostElement = uniqueElements[0]
    console.log('🔄 scrollToFirstError: Scrolling to topmost error field', {
      fieldId: topmostElement.fieldId,
      elementId: topmostElement.elementId,
      yPosition: topmostElement.yPosition,
      totalErrorElements: uniqueElements.length,
    })

    if (scrollToElement(topmostElement.elementId, 150)) {
      console.log(
        '✅ scrollToFirstError: Successfully scrolled to topmost field'
      )
      return true
    }
  }

  // Fallback: if we have team-related errors but couldn't find a specific field, try to scroll to team section
  const hasTeamErrors =
    fieldErrors.some(
      (error) => error.includes('team') || error.toLowerCase().includes('team')
    ) ||
    Object.keys(customError).some(
      (key) => key.includes('team') || key.toLowerCase().includes('team')
    )

  if (hasTeamErrors) {
    console.log(
      '🔄 scrollToFirstError: Attempting fallback scroll to team section'
    )
    if (scrollToElement('team-info-section', 150)) {
      console.log(
        '✅ scrollToFirstError: Scrolled to team info section as fallback'
      )
      return true
    }
  }

  console.warn('⚠️ scrollToFirstError: No error fields found to scroll to')
  return false
}

/**
 * 🔄 scrollToErrorAlert: Scrolls to the error alert container
 * @param alertId - The ID of the error alert element (default: 'form-error-alert')
 * @returns boolean - true if alert was found and scrolled to
 */
export const scrollToErrorAlert = (
  alertId: string = 'form-error-alert'
): boolean => {
  console.log('🔄 scrollToErrorAlert: Scrolling to error alert', { alertId })
  return scrollToElement(alertId, 120)
}

/**
 * 🔄 handleFormErrors: Main function to handle scrolling when form errors occur
 * First tries to scroll to specific error fields, then falls back to error alert
 * @param fieldErrors - Array of field IDs that have errors
 * @param customError - Map of field names to error messages
 * @param hasGeneralError - Whether there's a general error alert to show
 */
// Track last scroll to prevent duplicates
let lastScrollTime = 0
let lastErrorSignature = ''

export const handleFormErrors = (
  fieldErrors: string[],
  customError: Record<string, string>,
  hasGeneralError: boolean = false
): void => {
  // Create a signature of current errors to detect changes
  const errorSignature = JSON.stringify({
    fieldErrors: fieldErrors.sort(),
    customError,
  })
  const now = Date.now()

  // Prevent duplicate scrolls within 1 second for the same errors
  if (errorSignature === lastErrorSignature && now - lastScrollTime < 1000) {
    console.log('⏭️ handleFormErrors: Skipping duplicate scroll', {
      timeSinceLastScroll: now - lastScrollTime,
    })
    return
  }

  console.log('🔄 handleFormErrors: Processing form errors', {
    fieldErrorsCount: fieldErrors.length,
    customErrorCount: Object.keys(customError).length,
    hasGeneralError,
  })

  lastScrollTime = now
  lastErrorSignature = errorSignature

  // Small delay to ensure DOM updates are complete
  setTimeout(() => {
    // First try to scroll to the first error field
    const scrolledToField = scrollToFirstError(fieldErrors, customError)

    // If no field found and there's a general error, scroll to the alert
    if (!scrolledToField && hasGeneralError) {
      scrollToErrorAlert()
    }
  }, 100)
}
